import React from 'react';
import { MapPin, RefreshCw, Users, Search, Circle } from 'lucide-react';

// Live staff map — plots each app user's latest location (staff_location.php) on an
// OpenStreetMap/Leaflet map. While this page is open it keeps the field apps in
// "live" mode (set_live keep-alive) so their locations update in near-real-time.
const LEAFLET_CSS = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css';
const LEAFLET_JS  = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js';

const ensureLeaflet = () => new Promise((resolve, reject) => {
  if (window.L) return resolve(window.L);
  if (!document.getElementById('leaflet-css')) {
    const link = document.createElement('link');
    link.id = 'leaflet-css'; link.rel = 'stylesheet'; link.href = LEAFLET_CSS;
    document.head.appendChild(link);
  }
  const s = document.createElement('script');
  s.src = LEAFLET_JS;
  s.onload = () => resolve(window.L);
  s.onerror = () => reject(new Error('Could not load the map library'));
  document.body.appendChild(s);
});

const freshness = (min) => {
  if (min == null) return { color: '#94a3b8', label: '—' };
  if (min < 5)  return { color: '#16a34a', label: min <= 1 ? 'just now' : `${min} min ago` };
  if (min < 30) return { color: '#d97706', label: `${min} min ago` };
  if (min < 120) return { color: '#ef4444', label: `${min} min ago` };
  const h = Math.floor(min / 60);
  return { color: '#64748b', label: `${h}h ago` };
};

const initials = (name) => (name || '?').trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();

export default function LiveMap({ apiBaseUrl }) {
  const mapRef = React.useRef(null);
  const mapObj = React.useRef(null);
  const markers = React.useRef({});   // employee_id -> L.marker
  const didFit = React.useRef(false);
  const [staff, setStaff] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [selected, setSelected] = React.useState(null);

  // Init map once Leaflet is loaded.
  React.useEffect(() => {
    let cancelled = false;
    ensureLeaflet().then((L) => {
      if (cancelled || mapObj.current || !mapRef.current) return;
      const map = L.map(mapRef.current, { zoomControl: true }).setView([30.3753, 69.3451], 5); // Pakistan
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19, attribution: '© OpenStreetMap',
      }).addTo(map);
      mapObj.current = map;
      load(L);
    }).catch((e) => { setError(e.message); setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const keepAlive = React.useCallback((push = false) => {
    // Tell the field apps the office is watching → they switch to real-time updates.
    // `push` (only on open) sends a silent wake-push so they start sharing immediately.
    fetch(`${apiBaseUrl}/staff_location.php?action=set_live`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ seconds: 90, push }),
    }).catch(() => {});
  }, [apiBaseUrl]);

  const load = React.useCallback((L) => {
    const LL = L || window.L;
    fetch(`${apiBaseUrl}/staff_location.php`)
      .then(r => r.json())
      .then(j => {
        const rows = Array.isArray(j.data) ? j.data : (Array.isArray(j) ? j : []);
        setStaff(rows); setLoading(false); setError('');
        if (!LL || !mapObj.current) return;
        const map = mapObj.current;
        const seen = new Set();
        rows.forEach(s => {
          seen.add(s.employee_id);
          const f = freshness(s.minutes_ago);
          const pos = [s.lat, s.lng];
          let m = markers.current[s.employee_id];
          if (!m) {
            m = LL.circleMarker(pos, { radius: 9, color: '#fff', weight: 2, fillColor: f.color, fillOpacity: 1 }).addTo(map);
            markers.current[s.employee_id] = m;
          } else {
            m.setLatLng(pos).setStyle({ fillColor: f.color });
          }
          m.bindPopup(`<b>${s.name || 'Unknown'}</b><br>${s.designation || ''}<br><span style="color:${f.color}">● ${f.label}</span>`);
          m.bindTooltip(s.name || '', { permanent: false, direction: 'top' });
        });
        // Remove markers for staff no longer present
        Object.keys(markers.current).forEach(id => {
          if (!seen.has(Number(id))) { map.removeLayer(markers.current[id]); delete markers.current[id]; }
        });
        if (!didFit.current && rows.length > 0) {
          const b = LL.latLngBounds(rows.map(s => [s.lat, s.lng]));
          map.fitBounds(b.pad(0.25), { maxZoom: 14 });
          didFit.current = true;
        }
      })
      .catch(() => { setError('Could not load staff locations'); setLoading(false); });
  }, [apiBaseUrl]);

  // Poll + keep-alive every 15s while the page is open. First call wakes the apps (push).
  React.useEffect(() => {
    keepAlive(true);
    const t = setInterval(() => { load(); keepAlive(false); }, 15000);
    return () => {
      clearInterval(t);
      if (mapObj.current) { mapObj.current.remove(); mapObj.current = null; markers.current = {}; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const focus = (s) => {
    setSelected(s.employee_id);
    const map = mapObj.current, m = markers.current[s.employee_id];
    if (map) map.setView([s.lat, s.lng], 15, { animate: true });
    if (m) m.openPopup();
  };

  const filtered = staff.filter(s => {
    const q = search.trim().toLowerCase();
    return !q || (s.name || '').toLowerCase().includes(q) || (s.designation || '').toLowerCase().includes(q);
  });

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2"><MapPin size={20} className="text-red-500" /> Live Map</h2>
          <p className="text-xs text-slate-500">Where the crew is right now · opening this page switches the apps to live (otherwise they report every ~30 min)</p>
        </div>
        <button onClick={() => load()} className="px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600 text-sm font-semibold inline-flex items-center gap-2">
          <RefreshCw size={15} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Map */}
        <div className="col-span-12 lg:col-span-8">
          <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
            <div ref={mapRef} style={{ height: 'calc(100vh - 210px)', minHeight: 420, width: '100%', background: '#eef2f6' }} />
            {error && <div className="p-3 text-sm text-red-600 bg-red-50 border-t border-red-100">{error}</div>}
          </div>
        </div>

        {/* Side list */}
        <div className="col-span-12 lg:col-span-4">
          <div className="bg-white rounded-xl shadow-md border border-slate-200 flex flex-col" style={{ height: 'calc(100vh - 210px)', minHeight: 420 }}>
            <div className="p-4 border-b border-slate-200">
              <div className="flex items-center gap-2 mb-3">
                <Users size={16} className="text-slate-400" />
                <span className="font-bold text-slate-800">Staff online</span>
                <span className="ml-auto px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold">{staff.length}</span>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search staff…"
                  className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 focus:border-red-400 outline-none text-sm" />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center text-slate-400 text-sm"><RefreshCw size={22} className="mx-auto mb-2 animate-spin" />Loading…</div>
              ) : filtered.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  <MapPin size={30} className="mx-auto mb-2 opacity-40" />
                  <p className="text-sm font-medium">No locations yet</p>
                  <p className="text-xs mt-1">Staff appear here once their app shares location.</p>
                </div>
              ) : filtered.map(s => {
                const f = freshness(s.minutes_ago);
                return (
                  <button key={s.employee_id} onClick={() => focus(s)}
                    className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors flex items-center gap-3 ${selected === s.employee_id ? 'bg-red-50' : ''}`}>
                    <div className="w-9 h-9 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-xs font-bold shrink-0">{initials(s.name)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-800 text-sm truncate">{s.name || 'Unknown'}</div>
                      <div className="text-xs text-slate-400 truncate">{s.designation || '—'}</div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Circle size={9} fill={f.color} color={f.color} />
                      <span className="text-[11px] font-semibold" style={{ color: f.color }}>{f.label}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
